import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useFavoritePages from '../hooks/useFavoritePages';
import { routeIconForPath, routeLabels } from '../constants/routes';
import { FiStar, FiMoreHorizontal } from 'react-icons/fi';
import FavoritesManagerPanel from './FavoritesManagerPanel';
import { t } from '../i18n';

const PILL_GAP_PX = 4;

const getLabel = (fav) => fav.title || routeLabels[fav.path] || fav.path;
const getDisplayPath = (fav) => (fav.path === '/' ? '/products' : fav.path);
const isPathActive = (pathname, favPath) =>
    pathname === favPath ||
    (favPath !== '/' && favPath !== '/products' && pathname.startsWith(favPath + '/'));

const FavoritePill = React.forwardRef(({ fav, isActive, showLabel }, ref) => {
    const IconComponent = routeIconForPath(fav.path);
    const label = t(getLabel(fav));
    const style = {};
    if (fav.color) style.color = fav.color;
    if (fav.bold) style.fontWeight = 700;

    return (
        <Link
            ref={ref}
            to={getDisplayPath(fav)}
            className={`flex items-center px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={style}
            title={label}
        >
            {IconComponent && <IconComponent className="mr-1 text-sm flex-shrink-0" />}
            {showLabel && <span>{label}</span>}
        </Link>
    );
});

const HeaderFavorites = ({ mobile = false }) => {
    const { favorites } = useFavoritePages();
    const location = useLocation();
    const [panelOpen, setPanelOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(favorites.length);
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const manageBtnRef = useRef(null);

    const recalc = useCallback(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        const manageBtn = manageBtnRef.current;
        if (!container || !measure || !manageBtn) return;

        const available = container.clientWidth - manageBtn.offsetWidth - PILL_GAP_PX;
        if (available <= 0) {
            setVisibleCount(0);
            return;
        }

        const children = Array.from(measure.children);
        let used = 0;
        let count = 0;
        for (const child of children) {
            const w = child.getBoundingClientRect().width;
            const nextUsed = used + w + (count > 0 ? PILL_GAP_PX : 0);
            if (nextUsed > available) break;
            used = nextUsed;
            count += 1;
        }
        setVisibleCount(count);
    }, []);

    useLayoutEffect(() => {
        recalc();
        let frame = null;
        const ro = new ResizeObserver(() => {
            if (frame !== null) return;
            frame = requestAnimationFrame(() => {
                frame = null;
                recalc();
            });
        });
        if (containerRef.current) ro.observe(containerRef.current);
        return () => {
            if (frame !== null) cancelAnimationFrame(frame);
            ro.disconnect();
        };
    }, [recalc, favorites]);

    const hasFavorites = favorites.length > 0;

    if (mobile) {
        return (
            <>
                <div className="flex items-center min-w-0">
                    <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide px-2 py-1">
                        {hasFavorites ? (
                            <div className="flex items-center space-x-1 w-max">
                                {favorites.map(fav => (
                                    <FavoritePill
                                        key={fav.path}
                                        fav={fav}
                                        isActive={isPathActive(location.pathname, fav.path)}
                                        showLabel
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center text-xs text-gray-400 whitespace-nowrap">
                                <FiStar className="mr-1" />
                                <span>{t('Star pages in sidebar')}</span>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setPanelOpen(true)}
                        className="flex-shrink-0 flex items-center px-2 py-1 mr-2 rounded text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900 whitespace-nowrap border-l border-gray-200"
                        title={t('Manage featured pages')}
                    >
                        <FiMoreHorizontal className="text-sm" />
                    </button>
                </div>
                <FavoritesManagerPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
            </>
        );
    }

    return (
        <>
            <div ref={containerRef} className="flex items-center flex-1 min-w-0 mr-3 relative">
                {!hasFavorites && (
                    <div className="flex items-center text-xs text-gray-400 whitespace-nowrap">
                        <FiStar className="mr-1" />
                        <span className="hidden lg:inline">{t('Star pages in sidebar')}</span>
                    </div>
                )}

                {hasFavorites && (
                    <div className="flex items-center space-x-1 flex-1 min-w-0 overflow-hidden">
                        {favorites.slice(0, visibleCount).map(fav => (
                            <FavoritePill
                                key={fav.path}
                                fav={fav}
                                isActive={isPathActive(location.pathname, fav.path)}
                                showLabel
                            />
                        ))}
                    </div>
                )}

                <button
                    ref={manageBtnRef}
                    type="button"
                    onClick={() => setPanelOpen(true)}
                    className="ml-1 flex items-center px-2 py-1 rounded text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900 whitespace-nowrap flex-shrink-0"
                    title={t('Manage featured pages')}
                >
                    <FiMoreHorizontal className="text-sm" />
                    {hasFavorites && favorites.length > visibleCount && (
                        <span className="ml-1">+{favorites.length - visibleCount}</span>
                    )}
                </button>

                <div
                    ref={measureRef}
                    aria-hidden="true"
                    className="absolute top-0 left-0 flex items-center space-x-1 invisible pointer-events-none"
                    style={{ visibility: 'hidden' }}
                >
                    {favorites.map(fav => (
                        <FavoritePill
                            key={`m-${fav.path}`}
                            fav={fav}
                            isActive={false}
                            showLabel
                        />
                    ))}
                </div>
            </div>

            <FavoritesManagerPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
        </>
    );
};

export default HeaderFavorites;
